import type { AriaRole, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { type Tone, toneClasses } from "./tone";
import { Button } from "./button";
import { useProblemScope } from "@/lib/problem/problem-scope";
import { requestBugReport } from "@/lib/support/bug-report-request";

/**
 * A line of small print under what it is about: a word of caution under a
 * field, or why something did not go. The colour is the tone's words alone,
 * with no box, since the line belongs to the control above it.
 *
 * A class places it; its look is the tone's. A field's hint takes the lighter
 * caption colour instead, as the house modal draws the line under a field.
 */
export function Note({
  tone = "neutral",
  hint = false,
  id,
  role,
  className,
  children,
}: {
  tone?: Tone;
  /** The line under a field saying what to put in it, in the lighter caption colour. */
  hint?: boolean;
  id?: string;
  role?: AriaRole;
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      id={id}
      role={role}
      className={cn(
        "text-[11.5px] leading-[1.5]",
        hint ? "text-subtle-foreground" : toneClasses(tone, "words"),
        className,
      )}
    >
      {children}
    </p>
  );
}

/** How near the limit the words must be before their count is shown. */
const NEAR_LIMIT = 0.8;

/** Whether the words are longer than the server takes, so the field refuses them before sending. */
export function overLimit(words: string, max: number | undefined): boolean {
  return max !== undefined && words.length > max;
}

/**
 * The count under a field the server limits, shown only as the words near the
 * limit, and past it how much to remove, so a save is never refused for length.
 */
export function LimitNote({
  length,
  max,
  id,
  className,
}: {
  length: number;
  max: number;
  id?: string;
  className?: string;
}) {
  if (length < max * NEAR_LIMIT) return null;
  const count = `${String(length)} of ${String(max)} characters`;
  return length > max ? (
    <Note id={id} tone="destructive" role="alert" className={className}>
      {`${count}. Remove ${String(length - max)} to save.`}
    </Note>
  ) : (
    <Note id={id} hint className={className}>
      {count}
    </Note>
  );
}

/**
 * The line under a control whose act failed: what failed and why, Retry where the act can be
 * asked again, and Report, which every error offers.
 */
export function ProblemLine({
  onRetry,
  onReport,
  id,
  className,
  children,
}: {
  onRetry?: () => void;
  onReport: () => void;
  id?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Note
      id={id}
      tone="destructive"
      role="alert"
      className={cn("flex items-center justify-between gap-2", className)}
    >
      <span>{children}</span>
      <span className="flex shrink-0 items-center gap-1">
        {onRetry && (
          <Button
            variant="secondary"
            size="dense"
            type="button"
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
        <Button variant="link" size="dense" type="button" onClick={onReport}>
          Report
        </Button>
      </span>
    </Note>
  );
}

/**
 * The line under a field whose last save failed: the words are still in the
 * field, and Retry sends them again. Shown only while the failure stands, so
 * a field never claims to be saved when it is not. The field that shows it
 * also counts the words as unsaved (`useUnsavedDraft`) while it stands.
 */
export function NotSavedNote({
  failed,
  onRetry,
  id,
  className,
}: {
  failed: boolean;
  onRetry: () => void;
  id?: string;
  className?: string;
}) {
  const scope = useProblemScope();
  if (!failed) return null;
  return (
    <ProblemLine
      id={id}
      className={className}
      onRetry={onRetry}
      onReport={() => {
        requestBugReport({ Problem: "save", ...scope });
      }}
    >
      Not saved. Your words are kept here.
    </ProblemLine>
  );
}
